import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import consoleApi from "@/lib/consoleApi";
import { appBasePath } from "@/lib/routes";
import { StatusPill } from "@/components/console/StatusPill";
import { UI_PACK_CLASSIC, UI_PACK_OPTIONS, uiPackLabel } from "@/lib/uiPack";

export default function ConsoleCompanyDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [ownerForm, setOwnerForm] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [showOwner, setShowOwner] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["console-company", id],
    queryFn: async () => {
      const { data: res } = await consoleApi.get(`/console/companies/${id}`);
      return res;
    },
  });

  const { data: usersData, refetch: refetchUsers } = useQuery({
    queryKey: ["console-company-users", id],
    queryFn: async () => {
      const { data: res } = await consoleApi.get(`/console/companies/${id}/users`);
      return res;
    },
  });

  useEffect(() => {
    if (!data?.company) return;
    const c = data.company;
    setForm({
      name: c.name || "",
      slug: c.slug || "",
      legal_name: c.legal_name || "",
      contact_name: c.contact_name || "",
      contact_email: c.contact_email || "",
      contact_phone: c.contact_phone || "",
      email: c.email || "",
      phone: c.phone || "",
      notes: c.notes || "",
      currency: c.currency || "",
      ui_pack: c.ui_pack || UI_PACK_CLASSIC,
    });
  }, [data?.company]);

  useEffect(() => {
    if (!data?.company || window.location.hash !== "#features") return;
    const el = document.getElementById("features");
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [data?.company]);

  const saveMutation = useMutation({
    mutationFn: () => consoleApi.put(`/console/companies/${id}`, form),
    onSuccess: () => {
      toast.success("Company saved");
      queryClient.invalidateQueries({ queryKey: ["console-company", id] });
      queryClient.invalidateQueries({ queryKey: ["console-companies"] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Save failed"),
  });

  const statusMutation = useMutation({
    mutationFn: (status) => consoleApi.patch(`/console/companies/${id}/status`, { status }),
    onSuccess: () => {
      toast.success("Status updated");
      queryClient.invalidateQueries({ queryKey: ["console-company", id] });
      queryClient.invalidateQueries({ queryKey: ["console-companies"] });
      queryClient.invalidateQueries({ queryKey: ["console-dashboard"] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Status update failed"),
  });

  const uiPackMutation = useMutation({
    mutationFn: (ui_pack) => consoleApi.patch(`/console/companies/${id}/ui-pack`, { ui_pack }),
    onSuccess: (res) => {
      toast.success(res?.data?.message || "UI pack updated — ask restaurant users to log out and in again");
      queryClient.invalidateQueries({ queryKey: ["console-company", id] });
      queryClient.invalidateQueries({ queryKey: ["console-companies"] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "UI pack update failed"),
  });

  const featuresMutation = useMutation({
    mutationFn: (features) => consoleApi.put(`/console/companies/${id}/features`, { features }),
    onSuccess: () => {
      toast.success("Features updated");
      queryClient.invalidateQueries({ queryKey: ["console-company", id] });
      queryClient.invalidateQueries({ queryKey: ["console-companies"] });
      queryClient.invalidateQueries({ queryKey: ["console-dashboard"] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Feature update failed"),
  });

  const ownerMutation = useMutation({
    mutationFn: () =>
      consoleApi.post(`/console/companies/${id}/users`, {
        name: ownerForm.name.trim() || "Owner",
        email: ownerForm.email.trim(),
        password: ownerForm.password,
      }),
    onSuccess: () => {
      toast.success("Owner user created");
      setShowOwner(false);
      setOwnerForm({ name: "", email: "", password: "" });
      refetchUsers();
      queryClient.invalidateQueries({ queryKey: ["console-company", id] });
    },
    onError: (err) => {
      const msg =
        Object.values(err.response?.data?.errors || {}).flat()?.[0] ||
        err.response?.data?.message ||
        "Could not create user";
      toast.error(msg);
    },
  });

  const accessMutation = useMutation({
    mutationFn: async (userId = null) => {
      const payload = userId ? { user_id: userId } : {};
      const { data } = await consoleApi.post(`/console/companies/${id}/access`, payload);
      const base = appBasePath();
      const entryPath = base ? `${base}/console-access` : "/console-access";
      const url = `${window.location.origin}${entryPath}?token=${encodeURIComponent(data.token)}`;
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened) {
        throw new Error("Pop-up blocked — allow pop-ups to open the store in a new tab.");
      }
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Opened ${data.company?.name || "store"} without signing out the user.`);
    },
    onError: (err) => {
      toast.error(err.message || err.response?.data?.message || "Could not open store access");
    },
  });

  if (isLoading || !form) {
    return (
      <div className="flex justify-center py-20 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const company = data.company;
  const registry = data.features_registry || [];
  const features = company.features || {};
  const users = usersData?.users || [];

  return (
    <div>
      <Link
        to="/console/companies"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-slate-400 hover:text-teal-200"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Companies
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="console-display text-3xl font-bold tracking-tight text-white">{company.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill status={company.status} />
            <span
              className={
                company.ui_pack === "simple"
                  ? "rounded-full bg-violet-500/20 px-2.5 py-0.5 text-[11px] font-medium text-violet-200"
                  : "rounded-full bg-white/5 px-2.5 py-0.5 text-[11px] text-slate-400"
              }
            >
              UI: {company.ui_pack_label || uiPackLabel(company.ui_pack)}
            </span>
            <span className="text-[12px] text-slate-500">{company.slug}</span>
            <span className="text-[12px] text-slate-500">
              Registered{" "}
              {company.registered_at ? new Date(company.registered_at).toLocaleDateString() : "—"}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="console-btn console-btn-primary h-9"
            disabled={accessMutation.isPending || users.length === 0}
            onClick={() => accessMutation.mutate(null)}
          >
            {accessMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
            Access store
          </button>
          {["active", "suspended", "blocked"].map((s) => (
            <button
              key={s}
              type="button"
              disabled={company.status === s || statusMutation.isPending}
              className="console-btn console-btn-ghost h-9 capitalize"
              onClick={() => statusMutation.mutate(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <section className="console-card mb-5 p-5" data-testid="console-ui-pack-switch">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-[14px] font-semibold text-white">Restaurant UI</h2>
            <p className="mt-1 text-[12px] text-slate-500">
              Switch what this restaurant’s staff see after login. Takes effect on next login (ask them to log out / in).
            </p>
          </div>
          <span
            className={
              company.ui_pack === "simple"
                ? "rounded-full bg-violet-500/20 px-2.5 py-1 text-[11px] font-semibold text-violet-200"
                : "rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-slate-300"
            }
          >
            Active: {company.ui_pack_label || uiPackLabel(company.ui_pack)}
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {UI_PACK_OPTIONS.map((opt) => {
            const active = (company.ui_pack || UI_PACK_CLASSIC) === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={uiPackMutation.isPending || active}
                onClick={() => {
                  if (active) return;
                  if (
                    !window.confirm(
                      `Switch ${company.name} to ${opt.label}?\n\nStaff must log out and log in again to see the new UI.`,
                    )
                  ) {
                    return;
                  }
                  uiPackMutation.mutate(opt.value);
                }}
                className={
                  active
                    ? "rounded-xl border border-teal-400/50 bg-teal-500/15 p-4 text-start"
                    : "rounded-xl border border-white/10 bg-white/[0.03] p-4 text-start hover:border-teal-400/40 hover:bg-white/[0.06]"
                }
                data-testid={`console-ui-pack-${opt.value}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[15px] font-bold text-white">{opt.label}</span>
                  {active ? (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-teal-300">Current</span>
                  ) : uiPackMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  ) : (
                    <span className="text-[11px] font-semibold text-teal-300">Switch</span>
                  )}
                </div>
                <p className="mt-1.5 text-[12px] leading-snug text-slate-400">{opt.hint}</p>
              </button>
            );
          })}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="console-card p-5">
          <h2 className="mb-4 text-[14px] font-semibold text-white">Profile</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["name", "Name"],
              ["slug", "Slug"],
              ["legal_name", "Legal name"],
              ["currency", "Currency"],
              ["contact_name", "Contact name"],
              ["contact_email", "Contact email"],
              ["contact_phone", "Contact phone"],
              ["email", "Company email"],
              ["phone", "Phone"],
            ].map(([key, label]) => (
              <label key={key} className={key === "legal_name" || key === "name" ? "sm:col-span-2" : ""}>
                <span className="mb-1 block text-[11px] text-slate-500">{label}</span>
                <input
                  className="console-input"
                  value={form[key] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </label>
            ))}
            <label className="sm:col-span-2">
              <span className="mb-1 block text-[11px] text-slate-500">UI pack (also use Restaurant UI card above)</span>
              <select
                className="console-input"
                value={form.ui_pack || UI_PACK_CLASSIC}
                onChange={(e) => setForm((f) => ({ ...f, ui_pack: e.target.value }))}
              >
                {UI_PACK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} — {opt.hint}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] text-slate-500">
                Prefer the <strong className="font-semibold text-slate-400">Restaurant UI</strong> buttons at the top for an instant switch.
              </span>
            </label>
            <label className="sm:col-span-2">
              <span className="mb-1 block text-[11px] text-slate-500">Internal notes</span>
              <textarea
                className="console-input min-h-[80px] py-2"
                value={form.notes || ""}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>
          </div>
          <button
            type="button"
            className="console-btn console-btn-primary mt-4"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save profile
          </button>
        </section>

        <section id="features" className="console-card scroll-mt-6 p-5">
          <h2 className="mb-1 text-[14px] font-semibold text-white">Features</h2>
          <p className="mb-4 text-[12px] text-slate-500">
            Unlock premium modules for this company. Features marked{" "}
            <span className="font-semibold text-amber-300/90">Classic UI only</span> work on the
            Classic (old) UI pack for now — not Simple.
          </p>
          <div className="space-y-3">
            {registry.map((f) => {
              const on = Boolean(features[f.key]);
              const available = f.available !== false;
              return (
                <div
                  key={f.key}
                  className={`flex items-start justify-between gap-3 rounded-xl border border-white/5 px-3 py-3 ${
                    available ? "bg-white/[0.02]" : "opacity-50"
                  }`}
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-[13px] font-medium text-slate-100">{f.label}</div>
                      {f.classic_ui_only ? (
                        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
                          Classic UI only
                        </span>
                      ) : null}
                    </div>
                    <div className="text-[11px] text-slate-500">{f.description}</div>
                    {!available ? (
                      <div className="mt-1 text-[10px] uppercase tracking-wide text-amber-400/80">
                        Coming soon
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={!available || featuresMutation.isPending}
                    onClick={() =>
                      featuresMutation.mutate({
                        ...features,
                        [f.key]: !on,
                      })
                    }
                    className={`relative h-7 w-12 shrink-0 rounded-full transition ${
                      on ? "bg-teal-400" : "bg-slate-700"
                    }`}
                    aria-pressed={on}
                  >
                    <span
                      className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition ${
                        on ? "left-5" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>

          <h2 className="mb-3 mt-8 text-[14px] font-semibold text-white">Branches</h2>
          <ul className="space-y-2 text-[13px]">
            {(company.branches || []).map((b) => (
              <li key={b.id} className="flex justify-between rounded-lg bg-white/[0.02] px-3 py-2">
                <span className="text-slate-200">{b.name}</span>
                <span className="font-mono text-[11px] text-slate-500">{b.code}</span>
              </li>
            ))}
            {!company.branches?.length ? (
              <li className="text-slate-500">No branches</li>
            ) : null}
          </ul>
        </section>

        <section className="console-card p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[14px] font-semibold text-white">Users</h2>
            <button type="button" className="console-btn console-btn-ghost h-9" onClick={() => setShowOwner(true)}>
              <Plus className="h-4 w-4" />
              Add owner
            </button>
          </div>
          {users.length === 0 ? (
            <p className="text-[13px] text-slate-500">No users yet — provision an owner login.</p>
          ) : (
            <table className="w-full text-left text-[13px]">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                <tr className="border-b border-white/5">
                  <th className="py-2 font-medium">Name</th>
                  <th className="py-2 font-medium">Email</th>
                  <th className="py-2 font-medium">Roles</th>
                  <th className="py-2 font-medium">Branch</th>
                  <th className="py-2 font-medium text-right">Access</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/[0.04]">
                    <td className="py-2.5 text-slate-100">{u.name}</td>
                    <td className="py-2.5 text-slate-400">{u.email}</td>
                    <td className="py-2.5 text-slate-400">
                      {(u.roles || []).join(", ") || u.role || "—"}
                    </td>
                    <td className="py-2.5 text-slate-400">{u.default_branch?.code || "—"}</td>
                    <td className="py-2.5 text-right">
                      <button
                        type="button"
                        className="console-btn console-btn-ghost h-8 px-2 text-[12px]"
                        disabled={accessMutation.isPending || !u.is_active}
                        onClick={() => accessMutation.mutate(u.id)}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {showOwner ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="console-card w-full max-w-md p-6">
            <h2 className="console-display mb-4 text-xl font-bold text-white">Add owner user</h2>
            <div className="space-y-3">
              {[
                ["name", "Name", "text"],
                ["email", "Email *", "email"],
                ["password", "Password *", "password"],
              ].map(([key, label, type]) => (
                <label key={key} className="block">
                  <span className="mb-1 block text-[12px] text-slate-400">{label}</span>
                  <input
                    className="console-input"
                    type={type}
                    value={ownerForm[key]}
                    onChange={(e) => setOwnerForm((f) => ({ ...f, [key]: e.target.value }))}
                    required={key !== "name"}
                  />
                </label>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="console-btn console-btn-ghost" onClick={() => setShowOwner(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="console-btn console-btn-primary"
                disabled={ownerMutation.isPending || !ownerForm.email || !ownerForm.password}
                onClick={() => ownerMutation.mutate()}
              >
                {ownerMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
